import {
  sha256Hash,
  getClientIpAddress,
  getCookieData,
  BZNAV_USER_DATA,
  META_PIXEL_ACTION_SOURCE,
  META_PIXEL_BASIC_PROPERTIES,
  IS_CLIENT,
  IS_PRODUCTION
} from '@repo/common-utils';

import {
  MetaPixelEventNames,
  MetaPixelInitializeProps,
  MetaPixelParams,
  MetaPixelStandardEventNames,
  MetaPixelUserData,
  SetUserParams,
  META_PIXEL_STANDARD_EVENTS
} from '../types';

const STANDARD_EVENTS_SET = new Set<string>(META_PIXEL_STANDARD_EVENTS);

const isStandardEvent = (eventName: string): eventName is MetaPixelStandardEventNames => {
  return STANDARD_EVENTS_SET.has(eventName);
};

export class MetaPixelService {
  private readonly appId: string;
  private readonly pixelId: string;
  private readonly fbp?: string;
  private readonly fbc?: string;
  public clientIpAddress: string = '';
  public facebookLoginId: string = '';
  private isInit: boolean = false;
  private preservedEvents: { eventName: MetaPixelEventNames; eventParams?: MetaPixelParams }[] = [];
  private retryCount: number = 0;
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;
  private basicProperties: { user_data: Record<string, unknown>; action_source: string; event_source_url: string } = {
    user_data: {},
    action_source: 'website',
    event_source_url: ''
  };
  private eventId?: string;

  constructor({ appId, metaPixelId }: MetaPixelInitializeProps & { appId: string }) {
    this.appId = appId;
    this.pixelId = metaPixelId ?? '';
    this.fbp = getCookieData('_fbp');
    this.fbc = getCookieData('_fbc');
    this.init();
  }

  private async init() {
    if (!IS_CLIENT) return;

    const [clientIpResponse, facebookLoginIdResponse] = await Promise.allSettled([getClientIpAddress(), this.getFacebookLoginId()]);
    this.clientIpAddress = clientIpResponse.status === 'fulfilled' ? clientIpResponse.value : '';
    this.facebookLoginId = facebookLoginIdResponse.status === 'fulfilled' ? facebookLoginIdResponse.value : '';
    const savedUserData = getCookieData(BZNAV_USER_DATA);

    if (!savedUserData) {
      this.sendInitEvent();
    }
  }

  private generateMetaPixelServerApiData = (eventId?: string) => {
    if (eventId) {
      this.eventId = eventId;
    } else {
      this.eventId = `${this.appId}-event_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    }
    const dataToSave = {
      properties: this.basicProperties,
      event_id: this.eventId
    };
    sessionStorage.setItem(META_PIXEL_BASIC_PROPERTIES, JSON.stringify(dataToSave));
  };

  private setDefaultBasicProperties(userData?: MetaPixelUserData) {
    this.basicProperties = {
      user_data: {
        fbp: this.fbp,
        fbc: this.fbc,
        fb_login_id: this.facebookLoginId,
        client_ip_address: this.clientIpAddress,
        client_user_agent: window.navigator.userAgent,
        em: userData?.em ?? '',
        ph: userData?.ph ?? '',
        external_id: userData?.external_id ?? ''
      },
      action_source: getCookieData(META_PIXEL_ACTION_SOURCE) ?? 'website',
      event_source_url: window.location.origin
    };
  }

  async setUser({ id = '', email = '', phone = '' }: SetUserParams) {
    if (!IS_CLIENT) return;
    const [em, ph] = await Promise.all([sha256Hash(email), sha256Hash(phone)]);
    this.sendInitEvent({ external_id: id, em, ph });
  }

  sendEvent(eventName: MetaPixelEventNames, eventParams?: MetaPixelParams) {
    if (!this.isInit || !window.fbq) {
      this.preservedEvents.push({ eventName, eventParams });
      return;
    }

    const action = isStandardEvent(eventName) ? 'track' : 'trackCustom';
    const eventProperties: MetaPixelParams = Object.assign(
      {},
      {
        action_source: this.basicProperties.action_source,
        event_source_url: this.basicProperties.event_source_url,
        is_server: false
      },
      eventParams
    );

    let duplicationParams: { eventID: string } | undefined = undefined;
    if (this.eventId && eventName !== 'PageView') {
      duplicationParams = { eventID: this.eventId };
      this.generateMetaPixelServerApiData();
    }

    window.fbq(action, eventName, eventProperties, duplicationParams);
    this.printLog(eventName, eventProperties);
  }

  unsetUser() {
    if (this.isInit) {
      this.sendInitEvent();
    }
  }

  private async getFacebookLoginId(): Promise<string> {
    if (!window.FB || process.env.NEXT_PUBLIC_ZENV === 'loc') return '';
    return new Promise((resolve, reject) => {
      window.FB.getLoginStatus((response: { status: string; authResponse: { userID: string } }) => {
        if (response.status === 'connected') {
          resolve(response.authResponse.userID);
        } else {
          reject('facebook login id not found');
        }
      });
    });
  }

  private sendInitEvent(userData?: MetaPixelUserData) {
    if (window.fbq && this.clientIpAddress) {
      const metaProperties = sessionStorage.getItem(META_PIXEL_BASIC_PROPERTIES);
      if (metaProperties) {
        try {
          const parsedProperties = JSON.parse(metaProperties);
          if (parsedProperties.properties) {
            this.basicProperties = parsedProperties.properties;
            this.basicProperties.user_data = Object.assign({}, parsedProperties.properties.user_data, userData ?? { em: '', ph: '', external_id: '' });
          }
          this.generateMetaPixelServerApiData(parsedProperties.event_id);
        } catch {
          this.setDefaultBasicProperties(userData);
          this.generateMetaPixelServerApiData();
        }
      } else {
        this.setDefaultBasicProperties(userData);
        this.generateMetaPixelServerApiData();
      }
      this.isInit = true;
      window.fbq('init', `${this.pixelId}`, this.basicProperties.user_data);
      this.printLog('init', this.basicProperties);

      this.preservedEvents.forEach(({ eventName, eventParams }) => this.sendEvent(eventName, eventParams));
      this.preservedEvents = [];
    } else if (this.retryCount < 10) {
      if (this.retryTimeout) clearTimeout(this.retryTimeout);
      this.retryTimeout = setTimeout(() => {
        this.retryCount++;
        this.sendInitEvent(userData);
      }, 500);
    }
  }

  private printLog(event: string, params: Record<string, unknown>) {
    if (!IS_PRODUCTION && process.env.NEXT_PUBLIC_TRACKING_DEBUG_MODE === 'true') {
      console.info('%c[MetaPixel]', 'font-weight: bold;', event, params);
    }
  }
}
